import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * In-flight fetches keyed by page number, so concurrent prefetch and render
   * paths share one network request per page instead of duplicating it.
   * @type {Map<number, Promise<void>>}
   */
  #inflightPages = new Map();

  /** Reentrancy guard: the observer can fire while an append is in progress. */
  #isAppendingNext = false;

  /** Bumped on filter changes so stale in-flight fetches can't repopulate the cache. */
  #fetchGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    // Warm the cache one page beyond 'next' as well, so two pages of HTML are
    // ready before the user starts scrolling.
    this.#prefetchAhead();
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          // Start rendering the next page ~3 viewports before the sentinel is
          // reached, so a fast fling never catches up to the loading frontier.
          // (At the original 100px the append landed while the footer was already
          // on screen and scroll anchoring teleported the viewport past the new
          // cards.) Note: with a margin this large the sentinel may still be
          // inside it after an append, in which case no new intersection is ever
          // reported — #maybeRenderNextPage re-checks manually after each append.
          rootMargin: '300% 0px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    if (this.pages.has(pageNumber)) return;

    const existingFetch = this.#inflightPages.get(pageNumber);
    if (existingFetch) return existingFetch;

    const generation = this.#fetchGeneration;
    const fetchPromise = (async () => {
      try {
        const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
        // A filter change invalidates everything fetched before it
        if (generation === this.#fetchGeneration) {
          this.pages.set(pageNumber, pageContent);
        }
      } finally {
        this.#inflightPages.delete(pageNumber);
      }
    })();

    this.#inflightPages.set(pageNumber, fetchPromise);
    return fetchPromise;
  }

  /**
   * Keeps the HTML for the next pages cached ahead of the rendered frontier, so
   * a render never has to wait on the network. Fetches run concurrently.
   * @param {number} depth - How many pages ahead of the frontier to cache.
   */
  #prefetchAhead(depth = 2) {
    const nextPage = this.#getPage('next');
    if (!nextPage) return;

    for (let offset = 0; offset < depth; offset++) {
      this.#fetchSpecificPage(nextPage.page + offset);
    }
  }

  async #renderNextPage() {
    if (this.#isAppendingNext) return;

    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    this.#isAppendingNext = true;
    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      this.#eagerLoadLeadingImages(nextPageItemElements);
      await this.#appendInChunks(grid, Array.from(nextPageItemElements));

      history.pushState('', '', nextPage.url.toString());

      // Keep the fetch frontier ahead of the render frontier without waiting
      // for idle time — on a continuous scroll idle may come too late.
      this.#prefetchAhead();
    } finally {
      this.#isAppendingNext = false;
    }

    this.#maybeRenderNextPage();
  }

  /**
   * Appends cards a few at a time across animation frames, so a full page of
   * cards never blocks the main thread in one long task mid-scroll.
   * @param {HTMLElement} grid
   * @param {Element[]} elements
   * @param {number} [chunkSize]
   */
  async #appendInChunks(grid, elements, chunkSize = 8) {
    for (let i = 0; i < elements.length; i += chunkSize) {
      grid.append(...elements.slice(i, i + chunkSize));
      this.#aspectRatioHelper?.processNewElements();

      if (i + chunkSize < elements.length) {
        await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
      }
    }
  }

  /**
   * The first rows of an incoming page are on screen almost immediately during
   * a continuous scroll, but they arrive as loading="lazy" — and Safari's small
   * lazy-load threshold leaves them as white boxes mid-fling. Promote the main
   * image of the leading cards to eager. sizes="auto" is only valid on lazy
   * images, so the auto prefix is dropped in favour of the explicit widths
   * declared behind it.
   * @param {NodeListOf<Element>} cardElements
   * @param {number} [count]
   */
  #eagerLoadLeadingImages(cardElements, count = 8) {
    for (let i = 0; i < Math.min(count, cardElements.length); i++) {
      const img = cardElements[i]?.querySelector('img[loading="lazy"]');
      if (!(img instanceof HTMLImageElement)) continue;

      img.removeAttribute('loading');

      const sizes = img.getAttribute('sizes');
      if (sizes?.startsWith('auto')) {
        img.setAttribute('sizes', sizes.replace(/^auto,?\s*/, '') || '100vw');
      }
    }
  }

  /**
   * IntersectionObserver only reports crossings. After an append the sentinel
   * can still sit inside the very large rootMargin — no crossing, no callback —
   * which would stall the loop. Re-check its position manually and keep
   * rendering while the user is close enough to need the next page.
   */
  #maybeRenderNextPage() {
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext) return;

    const distanceBelowViewport = viewMoreNext.getBoundingClientRect().top - window.innerHeight;
    if (distanceBelowViewport < window.innerHeight * 3) {
      this.#renderNextPage();
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';

    if (!grid) return;

    // Query the DOM directly rather than this.refs.cards: refs update
    // asynchronously after mutations, and computing the frontier from a stale
    // list right after an append would re-render an already-rendered page.
    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    // Invalidate fetches that are still in flight for the pre-filter results
    this.#fetchGeneration++;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
